import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { setSpecialAdmin } from '../api/specialAdminApi';
import '../../components/user-management/UserProfile/styles/UserSecurity.css';
import './styles/UserSpecialAdmin.css';

const parseSpecialAdmin = (value) => value === true || value === 'true';

const UserSpecialAdmin = ({ user, setUser }) => {
  const { admin } = useAdminAuth();
  const isSuperAdmin = admin?.role === 'super-admin';

  const [enabled, setEnabled] = useState(parseSpecialAdmin(user?.isSpecialAdmin));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);

  useEffect(() => {
    setEnabled(parseSpecialAdmin(user?.isSpecialAdmin));
  }, [user?.id, user?.isSpecialAdmin]);

  if (!isSuperAdmin || !user?.id) {
    return null;
  }

  const closeDialog = () => {
    if (loading) {
      return;
    }
    setConfirmDialog(false);
    setPendingValue(null);
  };

  const handleToggleRequest = () => {
    setError('');
    setSuccess('');
    setPendingValue(!enabled);
    setConfirmDialog(true);
  };

  const handleConfirm = async () => {
    if (typeof pendingValue !== 'boolean') {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await setSpecialAdmin({
        userId: user.id,
        isSpecialAdmin: pendingValue,
      });

      setEnabled(pendingValue);
      setUser((prev) => ({
        ...prev,
        isSpecialAdmin: pendingValue,
      }));
      setSuccess(
        pendingValue
          ? 'Special admin access granted. The user may need to sign out and back in.'
          : 'Special admin access removed. The user may need to sign out and back in.'
      );
      closeDialog();
    } catch (err) {
      console.error('Failed to update special admin access:', err);
      setError(err?.message || 'Failed to update special admin access.');
      closeDialog();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-card user-section-card userSecurityCard userSpecialAdminCard" aria-busy={loading ? 'true' : 'false'}>
      {success ? (
        <div className="usAlert usAlertSuccess" role="status" aria-live="polite">
          {success}
        </div>
      ) : null}

      {error ? (
        <div className="usAlert usAlertError" role="alert" aria-live="polite">
          {error}
        </div>
      ) : null}

      <div className="usHeader">
        <div>
          <h3 className="usTitle">Special Admin Access</h3>
          <p className="usSub">
            Grants website admin login, payment sandbox controls, and demo tools on the main app.
          </p>
        </div>
      </div>

      <div className="userSpecialAdminCheckboxRow">
        <input
          id="specialAdminCheckbox"
          className="userSpecialAdminCheckbox"
          type="checkbox"
          checked={enabled}
          onChange={handleToggleRequest}
          disabled={loading}
        />
        <label className="userSpecialAdminCheckboxLabel" htmlFor="specialAdminCheckbox">
          <span className="userSpecialAdminCheckboxTitle">Grant special admin access</span>
          <span className="userSpecialAdminCheckboxSub">
            Enables website admin login, payment sandbox controls, and demo tools on the main app.
          </span>
        </label>
      </div>

      {confirmDialog ? (
        <div
          className="usModalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm special admin change"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeDialog();
            }
          }}
        >
          <div className="usModal">
            <h4 className="usModalTitle">
              {pendingValue ? 'Grant special admin access?' : 'Remove special admin access?'}
            </h4>
            <p className="usModalBody">
              {pendingValue
                ? `Grant special admin access to ${user.email || 'this user'}?`
                : `Remove special admin access from ${user.email || 'this user'}?`}
            </p>
            <div className="usModalActions">
              <button type="button" className="usBtn" onClick={closeDialog} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="usBtn usBtnPrimary" onClick={handleConfirm} disabled={loading}>
                {loading ? (
                  <>
                    <span className="usSpinner" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserSpecialAdmin;
